export const formatUnderScoreText = (text: string): string => {
   return text
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
};

/**
 * Builds the public certificate verification URL using index number and service id.
 * Use when CertificateApplication.id is not available (e.g. user certificate list from
 * GET /applicationconfirmed/user/:userId). GET /applicationconfirmed/verify-license
 * accepts indexNumber + serviceId as an alternative to applicationId.
 */
export const getCertificateVerificationUrlByIndexAndService = (
  indexNumber: string,
  serviceId: string
): string => {
  const frontendBaseUrl = typeof window !== 'undefined'
    ? window.location.origin
    : 'https://healthregulation.moh.gov.rw';
  const params = new URLSearchParams({
    indexNumber: indexNumber.trim(),
    serviceId: serviceId.trim(),
  });
  return `${frontendBaseUrl}/certificate-details/ViewCertificateDetails?${params.toString()}`;
};

/**
 * Builds the public certificate verification URL for a given application.
 * GET /api/v1/applicationconfirmed/verify-license expects applicationId to be
 * CertificateApplication.id (not ApplicationConfirmed.id). Passing an
 * ApplicationConfirmed UUID will return 404.
 *
 * @param certificateApplicationId - CertificateApplication.id (UUID). Required for verify-license.
 */
export const getCertificateVerificationUrl = (certificateApplicationId: string): string => {
   const frontendBaseUrl = typeof window !== 'undefined' 
      ? window.location.origin 
      : 'https://healthregulation.moh.gov.rw';
   return `${frontendBaseUrl}/certificate-details/ViewCertificateDetails?applicationId=${encodeURIComponent(certificateApplicationId)}`;
};

/**
 * Gets the profile image URL for certificate displays.
 * 
 * Priority order:
 * 1. applicationProfileDocumentPath - Profile picture saved at application approval time (for approved applications)
 * 2. profilePicture - Current user profile picture (for unapproved/preview applications)
 * 3. Empty string - If neither is available
 * 
 * @param applicationProfileDocumentPath - Profile picture path saved when application was approved (null/undefined for unapproved applications)
 * @param profilePicture - Current user profile picture path
 * @param baseUrl - Base URL for the API endpoint
 * @returns Full URL to the profile picture, or empty string if no picture is available
 * 
 * @example
 * // For approved applications
 * getCertificateProfileImageUrl(
 *   "978e4757-a5bc-4a87-9e14-7625620be6fc_1000330465.jpg", // Saved at approval
 *   "d2a253a5-50bb-465e-9d27-82022bc12d4c_1000338633.jpg", // Current profile
 *   "https://api.example.com"
 * ) // Returns URL with applicationProfileDocumentPath
 * 
 * @example
 * // For unapproved/preview applications
 * getCertificateProfileImageUrl(
 *   null, // Not yet approved
 *   "d2a253a5-50bb-465e-9d27-82022bc12d4c_1000338633.jpg", // Current profile
 *   "https://api.example.com"
 * ) // Returns URL with profilePicture
 */
export const getCertificateProfileImageUrl = (
  applicationProfileDocumentPath: string | null | undefined,
  profilePicture: string | null | undefined,
  baseUrl: string
): string => {
  // Prioritize applicationProfileDocumentPath (saved at approval time)
  // This ensures approved certificates always show the profile picture from when they were approved
  if (applicationProfileDocumentPath && applicationProfileDocumentPath.trim() !== "") {
    return `${baseUrl}/users/profile-picture?pictureName=${applicationProfileDocumentPath}`;
  }
  // Fall back to current user profile picture (for unapproved/preview applications)
  if (profilePicture && profilePicture.trim() !== "") {
    return `${baseUrl}/users/profile-picture?pictureName=${profilePicture}`;
  }
  // Return empty string if neither is available
  return "";
};

/**
 * Removes null, undefined, or empty keys from an object
 * @param obj Object to clean
 * @param options Configuration options
 * @returns New object with filtered keys
 * @author Cedric Mutesa
 */
export const removeEmptyKeys = <T extends Record<string, any>>(
   obj: T,
   options: {
      removeNull?: boolean; // Remove null and undefined values
      removeEmpty?: boolean; // Remove empty strings
      removeEmptyArrays?: boolean; // Remove empty arrays
      removeEmptyObjects?: boolean; // Remove empty objects
   } = { removeNull: true, removeEmpty: false, removeEmptyArrays: false, removeEmptyObjects: false },
): Partial<T> => {
   const { removeNull, removeEmpty, removeEmptyArrays, removeEmptyObjects } = options;

   return Object.entries(obj).reduce((acc, [key, value]) => {
      // Skip null/undefined values if configured
      if (removeNull && (value === null || value === undefined)) {
         return acc;
      }

      // Skip empty strings if configured
      if (removeEmpty && value === '') {
         return acc;
      }

      // Skip empty arrays if configured
      if (removeEmptyArrays && Array.isArray(value) && value.length === 0) {
         return acc;
      }

      // Skip empty objects if configured
      if (
         removeEmptyObjects &&
         typeof value === 'object' &&
         value !== null &&
         !Array.isArray(value) &&
         Object.keys(value).length === 0
      ) {
         return acc;
      }

      // Keep all other values
      return { ...acc, [key]: value };
   }, {} as Partial<T>);
};

/**
 * Builds query parameters string from an object with advanced options
 * @param params Object containing query parameters
 * @param options Configuration options
 * @returns Formatted query string (without the leading '?')
 * @author Cedric Mutesa
 */
export const buildQueryParams = (
   params: Record<string, any>,
   options: {
      skipNull?: boolean; // Skip null and undefined values
      skipEmpty?: boolean; // Skip empty strings
      arrayFormat?: 'comma' | 'bracket' | 'repeat'; // How to format array values
   } = { skipNull: true, skipEmpty: true, arrayFormat: 'repeat' },
) => {
   const { skipNull, skipEmpty, arrayFormat } = options;

   // Remove empty keys based on options
   params = removeEmptyKeys(params, {
      removeNull: skipNull,
      removeEmpty: skipEmpty,
   });
   return Object.entries(params)
      .filter(([, value]) => {
         if (skipNull && (value === null || value === undefined)) return false;
         if (skipEmpty && value === '') return false;
         return true;
      })
      .map(([key, value]) => {
         // Handle arrays based on specified format
         if (Array.isArray(value)) {
            if (arrayFormat === 'comma') {
               return `${encodeURIComponent(key)}=${value.map(encodeURIComponent).join(',')}`;
            } else if (arrayFormat === 'bracket') {
               return `${encodeURIComponent(key)}[]=${value.map(encodeURIComponent).join('&' + encodeURIComponent(key) + '[]=')}`;
            } else {
               // repeat (default)
               return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
            }
         }

         // Handle objects by converting to JSON string
         if (value !== null && typeof value === 'object') {
            return `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`;
         }

         // Handle primitive values
         return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      })
      .join('&');
};